import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useCare } from '../contexts/CareContext'
import { useToast, humanizeError } from '../components/shared/Toast'
import { callGinie, confirmationRows, INTENT_LABEL, HISTORY_LIMIT } from '../lib/ginie'
import { isWebSpeechAvailable, getSpeechRecognition, composerPlaceholder } from '../lib/voice'
import MedicationExtractor from '../components/medications/MedicationExtractor'
import DocumentForm from '../components/documents/DocumentForm'

const todayISO = () => new Date().toLocaleDateString('en-CA')

// Hidden entirely inside the native WebView, where the API is non-functional.
// See lib/voice.js for why a Capacitor-only check is not sufficient.
const VOICE_ENABLED = isWebSpeechAvailable()

let msgSeq = 0
const nextId = () => `m${++msgSeq}`

export default function GiniePage() {
  const { user } = useAuth()
  const { recipient, role } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [messages, setMessages] = useState([]) // { id, from: 'user'|'ginie', text, action? }
  const [history, setHistory] = useState([])   // [{ role, content }] sent to the model
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [listening, setListening] = useState(false)
  const [attachOpen, setAttachOpen] = useState(false)
  const [attachMode, setAttachMode] = useState(null) // 'prescription' | 'document'

  const scrollRef = useRef(null)
  const recognitionRef = useRef(null)
  const inputRef = useRef('')
  inputRef.current = input

  const canWrite = role === 'admin' || role === 'family_member'
  const name = recipient?.preferred_name || recipient?.full_name || 'them'

  const SUGGESTIONS = [
    `How has ${name} been this week?`,
    'Log this morning’s care',
    'Remind me to call the doctor on Thursday',
    `What medications is ${name} on?`,
    `${name} had a good day today`,
    'Log an incident',
  ]

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, busy])

  const addMessage = (m) => setMessages((list) => [...list, { id: nextId(), ...m }])

  const send = useCallback(async (raw) => {
    const text = (raw ?? inputRef.current).trim()
    if (!text || busy || !recipient) return

    setInput('')
    addMessage({ from: 'user', text })
    setBusy(true)

    const nextHistory = [...history, { role: 'user', content: text }].slice(-HISTORY_LIMIT)

    try {
      const reply = await callGinie(text, recipient.id, history)
      addMessage({
        from: 'ginie',
        text: reply.response,
        action: reply.confirmation_required
          ? { intent: reply.intent, data: reply.data || {}, state: 'pending' }
          : null,
        options: reply.intent === 'unclear' ? (reply.data?.options || []).slice(0, 4) : null,
      })
      setHistory([...nextHistory, { role: 'assistant', content: reply.response }].slice(-HISTORY_LIMIT))
    } catch (err) {
      addMessage({ from: 'ginie', text: humanizeError(err), isError: true })
    } finally {
      setBusy(false)
    }
  }, [busy, history, recipient])

  /* ── Voice input ─────────────────────────────────────────── */

  /**
   * Ask for the mic explicitly before starting recognition.
   *
   * In a WKWebView the permission prompt does not reliably fire off the back
   * of SpeechRecognition alone — getUserMedia triggers it, and the track is
   * released immediately since recognition captures audio itself.
   */
  async function requestMicPermission() {
    if (!navigator.mediaDevices?.getUserMedia) return true // nothing to ask; let recognition try
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      return true
    } catch {
      return false
    }
  }

  async function toggleMic() {
    if (!VOICE_ENABLED) return
    if (listening) {
      recognitionRef.current?.stop()
      return
    }

    const allowed = await requestMicPermission()
    if (!allowed) {
      addMessage({
        from: 'ginie',
        text: 'Microphone access is needed for voice input. Please allow it in your iPhone Settings → Safari → Microphone, then try again.',
        isError: true,
      })
      return
    }

    const SpeechRecognition = getSpeechRecognition()
    if (!SpeechRecognition) return
    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-CA'

    let transcript = ''
    recognition.onresult = (event) => {
      transcript = Array.from(event.results).map((r) => r[0].transcript).join('')
      setInput(transcript)
    }
    recognition.onerror = () => setListening(false)
    recognition.onend = () => {
      setListening(false)
      // Send what was heard, not the ref — state may not have flushed yet.
      if (transcript.trim()) send(transcript)
    }

    recognitionRef.current = recognition
    setListening(true)
    try {
      recognition.start()
    } catch {
      setListening(false)
    }
  }

  useEffect(() => () => recognitionRef.current?.stop(), [])

  /* ── Saving a confirmed action ───────────────────────────── */

  function setActionState(id, state) {
    setMessages((list) =>
      list.map((m) => (m.id === id ? { ...m, action: { ...m.action, state } } : m))
    )
  }

  async function saveAction(msg) {
    const { intent, data } = msg.action
    if (!canWrite) return toast.error('Only an admin or family member can save this.')
    setActionState(msg.id, 'saving')
    try {
      await writeIntent({ intent, data, recipientId: recipient.id, userId: user.id })
      setActionState(msg.id, 'saved')
      addMessage({ from: 'system', text: `Saved. ${INTENT_LABEL[intent]?.label || 'Entry'} added for ${name}.` })
    } catch (err) {
      setActionState(msg.id, 'pending')
      toast.error(humanizeError(err))
    }
  }

  /** Hand off to the existing full form, pre-opened via the quick-add sheet. */
  function editAction(msg) {
    const target = {
      log_care_note: '/?add=care-log',
      log_vitals: '/?add=vitals',
      log_incident: '/?add=incident',
      create_appointment: '/?add=appointment',
      create_task: '/tasks',
      log_medication: '/medications?tab=today',
    }[msg.action.intent]
    setActionState(msg.id, 'cancelled')
    navigate(target || '/')
  }

  if (!recipient) {
    return (
      <div className="stack">
        <h1 className="title">Ginie</h1>
        <div className="card muted" style={{ fontSize: 14 }}>
          Add someone to care for first — then Ginie can help.
        </div>
      </div>
    )
  }

  return (
    <div className="stack" style={{ height: '100%' }}>
      {/* Header */}
      <div className="row" style={{ gap: 10 }}>
        <div
          aria-hidden
          style={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            background: 'var(--teal)', color: '#fff', fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <i className="ti ti-sparkles" />
        </div>
        <div>
          <h1 className="title" style={{ fontSize: 24 }}>Ginie</h1>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Ask me anything about {name}&rsquo;s care
          </p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="stack" style={{ gap: 12, overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {messages.length === 0 && (
          <div className="stack" style={{ gap: 10 }}>
            <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}>
              Tell me what happened today, ask about {name}&rsquo;s week, or get
              something on the calendar. I&rsquo;ll always show you what I&rsquo;m
              about to save before I save it.
            </p>
            <div className="row wrap" style={{ gap: 8 }}>
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="chip" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => {
          if (m.from === 'user') {
            return (
              <div key={m.id} style={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                <div
                  style={{
                    background: 'var(--teal)', color: '#fff', padding: '10px 14px',
                    borderRadius: 16, borderBottomRightRadius: 4, fontSize: 15, lineHeight: 1.5,
                  }}
                >
                  {m.text}
                </div>
              </div>
            )
          }

          if (m.from === 'system') {
            return (
              <div key={m.id} className="row" style={{ gap: 8, alignSelf: 'flex-start' }}>
                <span className="badge badge-green">✓ {m.text}</span>
              </div>
            )
          }

          return (
            <div key={m.id} style={{ alignSelf: 'flex-start', maxWidth: '92%' }} className="stack">
              <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <div
                  aria-hidden
                  style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    background: 'var(--pa-light)', color: 'var(--teal)', fontSize: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <i className="ti ti-sparkles" />
                </div>
                <div
                  className="card"
                  style={{
                    borderTopLeftRadius: 4, fontSize: 15, lineHeight: 1.55,
                    borderColor: m.isError ? 'var(--danger)' : undefined,
                  }}
                >
                  {m.text}
                </div>
              </div>

              {m.options?.length > 0 && (
                <div className="row wrap" style={{ gap: 8, marginLeft: 36 }}>
                  {m.options.map((o) => (
                    <button key={o} type="button" className="chip" onClick={() => send(o)}>{o}</button>
                  ))}
                </div>
              )}

              {m.action && (
                <div style={{ marginLeft: 36 }}>
                  <ConfirmationCard
                    action={m.action}
                    canWrite={canWrite}
                    onSave={() => saveAction(m)}
                    onEdit={() => editAction(m)}
                    onCancel={() => setActionState(m.id, 'cancelled')}
                  />
                </div>
              )}
            </div>
          )
        })}

        {busy && (
          <div className="row" style={{ gap: 8, alignSelf: 'flex-start' }}>
            <span className="spinner" />
            <span className="muted" style={{ fontSize: 14 }}>Ginie is thinking…</span>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="row" style={{ gap: 8, paddingTop: 4 }}>
        <button
          type="button"
          onClick={() => setAttachOpen(true)}
          aria-label="Attach a prescription or document"
          disabled={busy}
          style={{
            width: 46, height: 46, borderRadius: '50%', flexShrink: 0, fontSize: 19,
            border: '1.5px solid var(--border)', background: 'var(--surface)',
            color: 'var(--muted)', cursor: 'pointer', opacity: busy ? 0.4 : 1,
          }}
        >
          <i className="ti ti-paperclip" aria-hidden />
        </button>
        {VOICE_ENABLED && (
          <button
            type="button"
            onClick={toggleMic}
            aria-label={listening ? 'Stop listening' : 'Speak to Ginie'}
            aria-pressed={listening}
            className={listening ? 'mic-live' : ''}
            style={{
              width: 46, height: 46, borderRadius: '50%', flexShrink: 0, fontSize: 19,
              border: listening ? '2px solid var(--teal)' : '1.5px solid var(--border)',
              background: listening ? 'var(--teal-light)' : 'var(--surface)',
              color: listening ? 'var(--teal)' : 'var(--muted)', cursor: 'pointer',
            }}
          >
            <i className="ti ti-microphone" aria-hidden />
          </button>
        )}
        <input
          className="input grow"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder={listening ? 'Listening…' : composerPlaceholder(name)}
          aria-label="Message Ginie"
          disabled={busy}
        />
        <button
          type="button"
          onClick={() => send()}
          disabled={busy || !input.trim()}
          aria-label="Send"
          style={{
            width: 46, height: 46, borderRadius: '50%', flexShrink: 0, fontSize: 18,
            border: 'none', background: 'var(--teal)', color: '#fff',
            opacity: busy || !input.trim() ? 0.4 : 1, cursor: 'pointer',
          }}
        >
          <i className="ti ti-arrow-up" aria-hidden />
        </button>
      </div>

      {attachOpen && (
        <AttachSheet
          onCancel={() => setAttachOpen(false)}
          onPick={(mode) => { setAttachOpen(false); setAttachMode(mode) }}
        />
      )}

      {attachMode === 'prescription' && (
        <div className="modal-scrim" onClick={(e) => e.target === e.currentTarget && setAttachMode(null)}>
          <div className="modal-panel">
            <MedicationExtractor
              onCancel={() => setAttachMode(null)}
              onDone={() => {
                setAttachMode(null)
                // Ginie never reads an attachment on its own — the family asks.
                send("I've uploaded a prescription. Ginie, can you read it and suggest what to add?")
              }}
            />
          </div>
        </div>
      )}

      {attachMode === 'document' && (
        <DocumentForm
          recipientId={recipient.id}
          onCancel={() => setAttachMode(null)}
          onDone={(doc) => {
            setAttachMode(null)
            const fileName = doc?.file_name || 'a document'
            send(`I've attached a document called ${fileName}.`)
          }}
        />
      )}
    </div>
  )
}

/** Two ways in: read a prescription, or just file a document. */
function AttachSheet({ onCancel, onPick }) {
  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20 }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>Attach</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>
        <div className="stack" style={{ gap: 10 }}>
          <button className="card card-tap" onClick={() => onPick('prescription')} style={{ textAlign: 'left', width: '100%' }}>
            <div className="row" style={{ gap: 10 }}>
              <span style={{ fontSize: 22 }} aria-hidden>📋</span>
              <div className="grow">
                <div style={{ fontWeight: 700 }}>Upload prescription or lab result</div>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                  Ginie reads it and suggests what to add.
                </div>
              </div>
              <span className="muted" aria-hidden>→</span>
            </div>
          </button>
          <button className="card card-tap" onClick={() => onPick('document')} style={{ textAlign: 'left', width: '100%' }}>
            <div className="row" style={{ gap: 10 }}>
              <span style={{ fontSize: 22 }} aria-hidden>📄</span>
              <div className="grow">
                <div style={{ fontWeight: 700 }}>Upload document</div>
                <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                  Store a letter, result, or form in Documents.
                </div>
              </div>
              <span className="muted" aria-hidden>→</span>
            </div>
          </button>
        </div>
        <div style={{ height: 8 }} />
      </div>
    </div>
  )
}

function ConfirmationCard({ action, canWrite, onSave, onEdit, onCancel }) {
  const meta = INTENT_LABEL[action.intent] || { icon: '📝', label: 'Save entry' }

  if (action.state === 'saved') {
    return <span className="badge badge-green">✓ Logged</span>
  }
  if (action.state === 'cancelled') {
    return <span className="badge">Cancelled</span>
  }

  const rows = confirmationRows(action.intent, action.data)

  return (
    <div className="card stack" style={{ gap: 10, borderColor: 'var(--pa)', borderWidth: 1.5 }}>
      <div style={{ fontWeight: 700 }}>{meta.icon} {meta.label}</div>

      {rows.length === 0 ? (
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>
          Nothing specific picked up — tap Edit to fill this in yourself.
        </p>
      ) : (
        <div className="stack" style={{ gap: 4 }}>
          {rows.map((r) => (
            <div key={`${r.label}-${r.value}`} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <span className="muted" style={{ fontSize: 13, minWidth: 96, flexShrink: 0 }}>{r.label}</span>
              <span style={{ fontSize: 14, fontWeight: 600 }}>{r.value}</span>
            </div>
          ))}
        </div>
      )}

      {!canWrite && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Only an admin or family member can save this.
        </p>
      )}

      <div className="row wrap" style={{ gap: 8 }}>
        <button
          className="btn btn-sm grow"
          onClick={onSave}
          disabled={!canWrite || action.state === 'saving' || rows.length === 0}
        >
          {action.state === 'saving' ? <span className="spinner" /> : 'Save this'}
        </button>
        <button className="btn btn-sm btn-secondary" onClick={onEdit} disabled={action.state === 'saving'}>
          Edit
        </button>
        <button className="btn btn-sm btn-ghost" onClick={onCancel} disabled={action.state === 'saving'}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/* ── Writes ───────────────────────────────────────────────────
   Each branch mirrors the shape the matching form writes, so a Ginie-created
   row is indistinguishable from a hand-entered one. */

const num = (v, float = false) => {
  if (v === '' || v == null) return null
  const n = float ? parseFloat(v) : parseInt(v, 10)
  return Number.isFinite(n) ? n : null
}

async function writeIntent({ intent, data, recipientId, userId }) {
  if (intent === 'log_care_note') {
    const { error } = await supabase.from('care_logs').insert({
      care_recipient_id: recipientId,
      logged_by: userId,
      logger_type: 'family',
      log_date: data.log_date || todayISO(),
      shift: data.shift || 'full_day',
      mood_morning: num(data.mood_morning),
      mood_afternoon: num(data.mood_afternoon),
      mood_evening: num(data.mood_evening),
      fluids_ml: num(data.fluids_ml),
      meals_json: data.meals_json || null,
      movement_json: data.movement_json || null,
      sleep_json: data.sleep_json || null,
      notes: data.notes || null,
      behaviour_fall: !!data.behaviour_fall,
      behaviour_agitation: !!data.behaviour_agitation,
      behaviour_confusion: !!data.behaviour_confusion,
      behaviour_wandering: !!data.behaviour_wandering,
    })
    if (error) {
      if (error.code === '23505') {
        throw new Error('There is already a log for that shift today — open it to edit instead.')
      }
      throw error
    }
    return
  }

  if (intent === 'log_vitals') {
    const { error } = await supabase.from('vitals_logs').insert({
      care_recipient_id: recipientId,
      logged_by: userId,
      log_date: data.log_date || todayISO(),
      bp_systolic: num(data.bp_systolic),
      bp_diastolic: num(data.bp_diastolic),
      fluids_ml: num(data.fluids_ml),
      blood_glucose: num(data.blood_glucose, true),
    })
    if (error) throw error
    return
  }

  if (intent === 'log_incident') {
    const { error } = await supabase.from('incidents').insert({
      care_recipient_id: recipientId,
      logged_by: userId,
      incident_date: data.incident_date || todayISO(),
      incident_time: data.incident_time || null,
      incident_type: data.incident_type || null,
      description: data.description || 'Logged via Ginie.',
      severity: ['minor', 'moderate', 'serious'].includes(data.severity) ? data.severity : 'minor',
      injury: !!data.injury,
      injury_description: data.injury ? data.injury_description || null : null,
    })
    if (error) throw error
    return
  }

  if (intent === 'create_task') {
    const { error } = await supabase.from('tasks').insert({
      care_recipient_id: recipientId,
      created_by: userId,
      title: data.title || 'Follow up',
      description: data.description || null,
      due_date: data.due_date || null,
      is_completed: false,
    })
    if (error) throw error
    return
  }

  if (intent === 'create_appointment') {
    const { error } = await supabase.from('appointments').insert({
      care_recipient_id: recipientId,
      created_by: userId,
      doctor_name: data.doctor_name || 'Appointment',
      specialty: data.specialty || null,
      appointment_date: data.appointment_date || todayISO(),
      appointment_time: data.appointment_time || null,
      purpose: data.purpose || null,
    })
    if (error) throw error
    return
  }

  if (intent === 'log_medication') {
    // The model is told to match against the active list, but it can still
    // return a name that no longer exists — resolve it here rather than
    // writing a dangling log.
    const { data: meds, error: medErr } = await supabase
      .from('medications')
      .select('id, name')
      .eq('care_recipient_id', recipientId)
      .eq('is_active', true)
    if (medErr) throw medErr

    const wanted = String(data.medication_name || '').trim().toLowerCase()
    const match = (meds || []).find((m) => m.name?.toLowerCase() === wanted)
      || (meds || []).find((m) => wanted && m.name?.toLowerCase().includes(wanted))
    if (!match) throw new Error(`I couldn't find "${data.medication_name}" in the medication list.`)

    const { error } = await supabase.from('medication_logs').insert({
      care_recipient_id: recipientId,
      medication_id: match.id,
      logged_by: userId,
      log_date: data.log_date || todayISO(),
      status: ['taken', 'missed', 'refused'].includes(data.status) ? data.status : 'taken',
    })
    if (error) {
      // medication_logs is unique on (medication_id, log_date).
      if (error.code === '23505') throw new Error(`${match.name} is already logged for that day.`)
      throw error
    }
    return
  }

  throw new Error('That is not something I can save yet.')
}
